import React, { useState } from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { usePatchDraft } from '@/hooks/use-onboarding-interview';
import { RECURRENCE_INTERVAL } from '@/types/recurrence-interval';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { parseCurrency } from '@/utils/parse-currency';

/** O que o sheet edita: um serviço do cartão, do jeito que ele chega. */
/** Uma vez por semana: o plano que o prestador quase sempre quer dizer. */
const DEFAULT_MONTHLY_QUOTA = 4;

export interface EditableService {
  name: string;
  defaultPrice?: number | null;
  durationMinutes?: number | null;
  recurring?: boolean;
  /** Quantas sessões o plano dá por mês. Nulo fora do plano. */
  subscriptionQuota?: number | null;
}

/** O sheet aberto para criar um serviço novo, em vez de corrigir um existente. */
export const NEW_SERVICE: EditableService = {
  name: '',
  defaultPrice: null,
  durationMinutes: null,
  recurring: false,
  subscriptionQuota: null,
};

/**
 * Valor, tempo e forma de cobrança de um serviço.
 *
 * É o mesmo sheet nos dois cartões que mexem em serviço, e por isso mora
 * sozinho: duplicado, "plano mensal" existiria num e não no outro.
 *
 * `onSave` existe porque nem sempre a edição vai direto para o rascunho — no
 * cartão de marcar, o serviço só é gravado quando ele toca em "Pronto".
 */
export function ServiceEditSheet({
  service,
  interval,
  onSave,
  onClose,
}: {
  service: EditableService | null;
  /** A cadência que "plano mensal" grava. Vem do ramo. */
  interval?: RECURRENCE_INTERVAL;
  /**
   * Quem recebe o serviço editado. Ausente = grava direto no rascunho, que é
   * o comportamento do cartão de conferência.
   */
  onSave?: (service: {
    name: string;
    defaultPrice: number | null;
    durationMinutes: number | null;
    isSubscription: boolean;
    subscriptionQuota: number | null;
    subscriptionQuotaPeriod: QUOTA_PERIOD | null;
    recurringBookingMode: RECURRING_BOOKING_MODE;
    recurringBillingInterval: RECURRENCE_INTERVAL | null;
  }) => void;
  onClose: () => void;
}) {
  const patch = usePatchDraft();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [quota, setQuota] = useState(String(DEFAULT_MONTHLY_QUOTA));
  const [current, setCurrent] = useState<EditableService | null>(null);

  /** Serviço sem nome é o "Adicionar": aí o nome é o primeiro campo. */
  const isNew = service !== null && service.name === '';

  // O sheet só monta uma vez; ao trocar de serviço, recarrega os campos.
  if (service && service !== current) {
    setCurrent(service);
    setName(service.name);
    setPrice(service.defaultPrice != null ? String(service.defaultPrice) : '');
    setDuration(
      service.durationMinutes != null ? String(service.durationMinutes) : '',
    );
    setRecurring(!!service.recurring);
    setQuota(String(service.subscriptionQuota ?? DEFAULT_MONTHLY_QUOTA));
  }

  const handleSave = async () => {
    if (!service) return;
    const finalName = name.trim();
    // Nome é a chave do merge no backend: sem ele o patch criaria lixo.
    if (!finalName) return;

    // Campo vazio é resposta, não falta: "é orçado" e "leva o tempo que
    // levar" — o backend guarda nulo e para de perguntar.
    const parsedPrice = price.trim() ? (parseCurrency(price) ?? null) : null;
    const parsedDuration = duration.trim() ? Number(duration) : null;
    const minutes = parsedDuration && parsedDuration > 0 ? parsedDuration : null;
    // Assinatura sem tempo de atendimento é configuração que o backend recusa
    // — não dá para reservar horário nenhum. O botão fica travado antes disso.
    if (recurring && !minutes) return;

    const parsedQuota = Number(quota);
    const monthlyQuota =
      Number.isFinite(parsedQuota) && parsedQuota > 0
        ? Math.min(Math.trunc(parsedQuota), 31)
        : DEFAULT_MONTHLY_QUOTA;

    const edited = {
      name: finalName,
      defaultPrice: parsedPrice,
      durationMinutes: minutes,
      // Plano é assinatura, e assinatura precisa dizer quantas sessões
      // entrega: sem a cota o "plano mensal" não teria contra o que comparar
      // na hora de o cliente marcar.
      isSubscription: recurring,
      subscriptionQuota: recurring ? monthlyQuota : null,
      subscriptionQuotaPeriod: recurring ? QUOTA_PERIOD.MONTH : null,
      recurringBookingMode: recurring
        ? RECURRING_BOOKING_MODE.AUTO
        : RECURRING_BOOKING_MODE.OFF,
      recurringBillingInterval: recurring
        ? (interval ?? RECURRENCE_INTERVAL.MONTHLY)
        : null,
    };

    if (onSave) onSave(edited);
    else await patch.mutateAsync({ payload: { services: [edited] } });
    setCurrent(null);
    onClose();
  };

  return (
    <Sheet
      open={!!service}
      onOpenChange={open => {
        if (!open) {
          setCurrent(null);
          onClose();
        }
      }}
      side="bottom"
    >
      <SheetContent className="gap-4">
        <ThemedText className="text-lg font-bold text-foreground">
          {isNew ? 'Novo serviço' : service?.name}
        </ThemedText>

        {isNew ? (
          <View className="gap-2">
            <ThemedText className="text-sm font-medium text-foreground">
              Como você chama esse serviço
            </ThemedText>
            <Input
              value={name}
              onChangeText={setName}
              placeholder="Ex: Corte infantil"
              autoFocus
            />
          </View>
        ) : null}

        <View className="gap-2">
          <ThemedText className="text-sm font-medium text-foreground">
            Quanto você cobra
          </ThemedText>
          <Input
            value={price}
            onChangeText={setPrice}
            placeholder="Ex: 45"
            keyboardType="decimal-pad"
          />
          <ThemedText className="text-xs text-foreground/45">
            Deixe em branco se o valor é combinado caso a caso.
          </ThemedText>
        </View>

        <View className="gap-2">
          <ThemedText className="text-sm font-medium text-foreground">
            Quanto tempo leva (minutos)
          </ThemedText>
          <Input
            value={duration}
            onChangeText={setDuration}
            placeholder="Ex: 45"
            keyboardType="number-pad"
          />
          <ThemedText className="text-xs text-foreground/45">
            Deixe em branco se cada atendimento leva o tempo que precisar.
          </ThemedText>
        </View>

        <View className="gap-2">
          <ThemedText className="text-sm font-medium text-foreground">
            Como você cobra
          </ThemedText>
          <View className="flex-row gap-2">
            <ChargeOption
              label="Por atendimento"
              selected={!recurring}
              onPress={() => setRecurring(false)}
            />
            <ChargeOption
              label="Plano mensal"
              selected={recurring}
              onPress={() => setRecurring(true)}
            />
          </View>
          <ThemedText className="text-xs text-foreground/45">
            {recurring
              ? 'O cliente paga todo mês e marca as vezes que o plano dá, quando quiser.'
              : 'O cliente paga cada vez que é atendido.'}
          </ThemedText>
          {recurring ? (
            <View className="gap-2 pt-1">
              <ThemedText className="text-sm font-medium text-foreground">
                Quantas vezes por mês
              </ThemedText>
              <Input
                value={quota}
                onChangeText={setQuota}
                placeholder="Ex: 4"
                keyboardType="number-pad"
              />
              <ThemedText className="text-xs text-foreground/45">
                O que não for usado não acumula para o mês seguinte.
              </ThemedText>
            </View>
          ) : null}

          {recurring && !duration.trim() ? (
            <ThemedText className="text-xs text-error-600">
              Plano mensal precisa do tempo de atendimento.
            </ThemedText>
          ) : null}
        </View>

        <Button
          onPress={() => void handleSave()}
          disabled={
            patch.isPending ||
            !name.trim() ||
            (recurring && !duration.trim())
          }
        >
          <ThemedText className="font-semibold text-white">
            {patch.isPending ? 'Salvando...' : 'Salvar'}
          </ThemedText>
        </Button>
      </SheetContent>
    </Sheet>
  );
}

/** Um lado do segmentado "como você cobra". */
function ChargeOption({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <PressableScale
      onPress={onPress}
      className={`flex-1 items-center rounded-xl border py-2.5 ${
        selected
          ? 'border-primary bg-primary/10'
          : 'border-border/60 bg-neutral-50'
      }`}
    >
      <ThemedText
        className={`text-sm font-semibold ${
          selected ? 'text-primary' : 'text-foreground/60'
        }`}
      >
        {label}
      </ThemedText>
    </PressableScale>
  );
}
